import posthog from 'posthog-js';
import { posthogReplayPrivacy } from '@mts/shared/posthogReplayPrivacy';
import { getOrCreateVisitorId } from '../services/visitorTracking';
import { getGeneratedDisplayName } from '../services/identityDisplayName';
import { COOKIE_CATEGORIES, isConsentGiven } from '../services/cookieConsentState';

const POSTHOG_KEY = String(import.meta.env.VITE_POSTHOG_KEY || '').trim();
const POSTHOG_HOST = String(import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com').trim();
const POSTHOG_PERSISTENCE_NAME = 'mts_web_identity_v2';

let initialized = false;
let consentListenerRegistered = false;
let tenantContext = null;
let captureEnabled = false;
let userContext = null;
let identityResetPending = false;

function hasAnalyticsConsent() {
  return isConsentGiven(COOKIE_CATEGORIES.ANALYTICS);
}

function restoreContext() {
  if (identityResetPending) {
    posthog.reset();
    identityResetPending = false;
  }
  for (const key of ['tenant_id', 'tenant_slug', 'tenant_name']) posthog.unregister(key);
  if (tenantContext) posthog.register(tenantContext);
  if (userContext) identifyPostHogUser(userContext);
}

export function initializePostHog() {
  if (!hasAnalyticsConsent() || initialized || !POSTHOG_KEY || POSTHOG_KEY.startsWith('phx_') || typeof window === 'undefined') {
    return;
  }

  const anonymousVisitorId = getOrCreateVisitorId();
  const visitorDisplayName = getGeneratedDisplayName(anonymousVisitorId);

  posthog.init(POSTHOG_KEY, {
    ...posthogReplayPrivacy,
    api_host: POSTHOG_HOST,
    defaults: '2026-05-30',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    persistence_name: POSTHOG_PERSISTENCE_NAME,
    bootstrap: {
      distinctID: anonymousVisitorId,
      isIdentifiedID: false,
    },
    before_send: (event) => !hasAnalyticsConsent() ? null : event ? ({
      ...event,
      properties: {
        visitor_id: anonymousVisitorId,
        display_name: visitorDisplayName,
        profile_type: 'visitor',
        ...event.properties,
      },
    }) : event,
    tracing_headers: ['api.mytownspotlight.com'],
  });

  // Recover from a persisted SDK opt-out only after current consent is confirmed.
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing({ captureEventName: false });
  }
  initialized = true;
  captureEnabled = true;
  posthog.register({
    app_name: 'web',
    visitor_id: anonymousVisitorId,
    display_name: visitorDisplayName,
    profile_type: 'visitor',
  });
  restoreContext();
}

export function initializePostHogIfAnalyticsConsentGranted() {
  if (!isConsentGiven(COOKIE_CATEGORIES.ANALYTICS)) {
    if (initialized && captureEnabled) {
      posthog.stopSessionRecording();
      posthog.opt_out_capturing();
      captureEnabled = false;
    }
    return;
  }
  if (!initialized) {
    initializePostHog();
  } else if (!captureEnabled) {
    posthog.opt_in_capturing({ captureEventName: false });
    captureEnabled = true;
    restoreContext();
    // No overrides: retain the project's sampling and recording eligibility.
    posthog.startSessionRecording();
  }
}

export function registerPostHogConsentListener() {
  if (consentListenerRegistered || typeof window === 'undefined') {
    return;
  }

  consentListenerRegistered = true;
  window.addEventListener('mts:consent:changed', () => {
    initializePostHogIfAnalyticsConsentGranted();
  });
}

export function initializeConsentAwarePostHog() {
  registerPostHogConsentListener();
  initializePostHogIfAnalyticsConsentGranted();
}

export function identifyPostHogUser({ id, email, name, displayName, visitorId }) {
  if (!id) return;
  userContext = { id, email, name, displayName, visitorId };
  if (!initialized || !captureEnabled || !hasAnalyticsConsent()) {
    return;
  }

  posthog.identify(id, {
    display_name: displayName || name || null,
    email: email || null,
    full_name: name || null,
    visitor_id: visitorId || getOrCreateVisitorId(),
    profile_type: 'user',
  });

  posthog.register({
    visitor_id: visitorId || getOrCreateVisitorId(),
    display_name: displayName || name || null,
    profile_type: 'user',
  });
}

export function resetPostHogIdentity() {
  userContext = null;
  identityResetPending = initialized;
  if (!initialized || !captureEnabled || !hasAnalyticsConsent()) {
    return;
  }

  posthog.reset();
  identityResetPending = false;
}

export function setPostHogTenantContext(tenant) {
  const tenantId = String(tenant?.id || '').trim();
  if (!tenantId) {
    tenantContext = null;
    if (initialized && captureEnabled && hasAnalyticsConsent()) {
      posthog.unregister('tenant_id');
      posthog.unregister('tenant_slug');
      posthog.unregister('tenant_name');
    }
    return;
  }

  tenantContext = {
    tenant_id: tenantId,
    tenant_slug: tenant?.slug || null,
    tenant_name: tenant?.name || null,
  };
  if (initialized && captureEnabled && hasAnalyticsConsent()) {
    posthog.register(tenantContext);
  }
}
